import type { Comic, ComicSummary, PlayerRef } from '@panels/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ComicStrip } from '../components/ComicStrip.js';
import { EmptyNote, ErrorNote, Screen, Spinner } from '../components/ui.js';
import { ApiError, getComic, listComics, type ComicQuery } from '../lib/api.js';
import { useVisiblePoll } from '../lib/usePoll.js';

/** The gallery quietly refreshes this often while open, so new titles appear (Round 2 §1.4). */
const GALLERY_POLL_MS = 20_000;

/**
 * "Show only…" filters, as data. Favorites is the first; reaction filters ("comics
 * with a genius panel") slot in here later as more entries, each mapping to a
 * query parameter the API understands.
 */
type ViewFilterId = 'favorites';
interface ViewFilter {
  id: ViewFilterId;
  label: string;
  apply: (query: ComicQuery) => ComicQuery;
  empty: string;
}
const VIEW_FILTERS: ViewFilter[] = [
  {
    id: 'favorites',
    label: '★ Favorites',
    apply: (query) => ({ ...query, favorites: true }),
    empty: 'No favorites yet. Star a comic to keep it here.',
  },
];

export function Gallery() {
  const navigate = useNavigate();
  const [comics, setComics] = useState<ComicSummary[]>([]);
  const [everyone, setEveryone] = useState<PlayerRef[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [views, setViews] = useState<ViewFilterId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const queryFor = useCallback(
    (players: string[], activeViews: ViewFilterId[]): ComicQuery =>
      VIEW_FILTERS.filter((f) => activeViews.includes(f.id)).reduce((q, f) => f.apply(q), { players } as ComicQuery),
    [],
  );

  const load = useCallback(async (players: string[], activeViews: ViewFilterId[], quiet = false) => {
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    try {
      const result = await listComics(queryFor(players, activeViews));
      setComics(result.comics);
      // The chip list comes from the unfiltered set, so a filter never hides the
      // control you would use to undo it.
      if (players.length === 0 && activeViews.length === 0) {
        const seen = new Map<string, PlayerRef>();
        for (const comic of result.comics) {
          for (const p of comic.participants) if (!seen.has(p.id)) seen.set(p.id, p);
        }
        setEveryone([...seen.values()].sort((a, b) => a.displayName.localeCompare(b.displayName)));
      }
    } catch (err) {
      // A background refresh failing is not worth a banner.
      if (!quiet) setError(err instanceof ApiError ? err.message : 'Could not load your comics');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [queryFor]);

  useEffect(() => {
    void load(selected, views);
  }, [load, selected, views]);

  useVisiblePoll(() => load(selected, views, true), GALLERY_POLL_MS);

  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const toggleView = (id: ViewFilterId) =>
    setViews((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const activeView = VIEW_FILTERS.find((f) => views.includes(f.id));

  const shuffle = async () => {
    try {
      const result = await listComics({ ...queryFor(selected, views), shuffle: true });
      const pick = result.comics[0];
      if (pick) navigate(`/comic/${pick.sheetId}`);
    } catch {
      setError('Could not find a comic to show');
    }
  };

  return (
    <Screen
      title="Comics"
      back="/"
      action={
        comics.length > 0 ? (
          <button type="button" className="link-button" onClick={shuffle}>
            Shuffle
          </button>
        ) : null
      }
      wide
    >
      <div className="chips view-filters" aria-label="Show only">
        {VIEW_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`chip ${views.includes(filter.id) ? 'selected' : ''}`}
            onClick={() => toggleView(filter.id)}
            aria-pressed={views.includes(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {everyone.length > 0 ? (
        <div className="chips">
          {everyone.map((player) => (
            <button
              key={player.id}
              type="button"
              className={`chip ${selected.includes(player.id) ? 'selected' : ''}`}
              onClick={() => toggle(player.id)}
              aria-pressed={selected.includes(player.id)}
            >
              {player.displayName}
            </button>
          ))}
          {selected.length > 0 ? (
            <button type="button" className="chip clear" onClick={() => setSelected([])}>
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {selected.length > 1 ? (
        <p className="muted small">Showing comics with everyone selected in them.</p>
      ) : null}

      {error ? <ErrorNote onRetry={() => void load(selected, views)}>{error}</ErrorNote> : null}
      {loading && comics.length === 0 ? <Spinner /> : null}
      {!loading && comics.length === 0 && !error ? (
        <EmptyNote>
          {activeView && selected.length === 0
            ? activeView.empty
            : selected.length > 0 || activeView
              ? 'No comics match all of those filters yet.'
              : 'No comics yet. Play a round!'}
        </EmptyNote>
      ) : null}

      <ul className="gallery">
        {comics.map((comic) => (
          <li key={comic.sheetId}>
            <Link to={`/comic/${comic.sheetId}`} className="gallery-item">
              <ComicThumb summary={comic} />
              <div className="gallery-meta">
                <strong>
                  {comic.favorite ? <span className="favorite-mark" aria-label="Favorite">★ </span> : null}
                  {comic.title ?? `${comic.starter.displayName}'s comic`}
                </strong>
                <span className="muted">
                  {comic.status === 'abandoned' ? 'unfinished · ' : ''}
                  {comic.participants.map((p) => p.displayName).join(', ')}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Screen>
  );
}

/**
 * Thumbnails are fetched only once they scroll into view — the list itself carries
 * no stroke data, which keeps the gallery query small however many comics exist.
 */
function ComicThumb({ summary }: { summary: ComicSummary }) {
  const ref = useRef<HTMLDivElement>(null);
  const [comic, setComic] = useState<Comic | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || comic) return;

    if (typeof IntersectionObserver === 'undefined') {
      // No observer to lean on: just load it rather than show a placeholder forever.
      getComic(summary.sheetId)
        .then(setComic)
        .catch(() => undefined);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        getComic(summary.sheetId)
          .then(setComic)
          .catch(() => undefined);
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [summary.sheetId, comic]);

  return (
    <div ref={ref} className="gallery-thumb">
      {comic ? (
        <ComicStrip comic={comic} replay={false} attribution={false} />
      ) : (
        <div className="thumb-placeholder" aria-hidden="true" />
      )}
    </div>
  );
}
